import FHIRFetcher from 'data/FHIRFetcher';
import HooksPoster from 'data/HooksPoster';
import SendGridPoster from 'data/SendGridPoster';
import type { Bundle, BundleEntry, PlanDefinitionAction, Questionnaire } from 'fhir/r4';
import {
  EMAIL_TEMPLATES,
  REFERRAL_SURVEYS,
  REFERRAL_SUBJECT_LINES,
  REFERRAL_TYPE_TO_EMAIL,
  TEXT_MESSAGE_TEMPLATES_PINPOINT,
} from 'fhir-sdoh/common/constants';
import * as fhirpath from 'fhirpath';
import moment from 'moment';
import { useEffect } from 'react';
import React from 'react';
import { List, Label, Loader, Popup, Confirm, Icon, Form, Modal } from 'semantic-ui-react';
import type { DropdownItemProps, DropdownProps } from 'semantic-ui-react';
import { apiClient } from 'services/api-client';
import useSWR from 'swr';

import { useAuthContext } from '../../../../../contexts/AuthContext';
import { Intervene, FHIRProxyClient } from '../../../../../fhir-sdoh';
// `../../../../types/flat-fhir` resolved to `components/types/flat-fhir`, which
// does not exist; the module is `src/types/flat-fhir`, five levels up.
import type { FlatCarePlan } from '../../../../../types/flat-fhir';
import * as FHIRUtils from '../../../../../utils/FHIRUtils';
import type { ResourceEntry } from '../../../../../utils/bundleNarrowing';
import { mapKey } from '../../../../../utils/mapKey';
import type {
  StepCarePlanContext,
  StepConditionFlat,
  StepMutate,
  StepPatientIdentity,
  StepTasks,
} from '../../types';

/**
 * One selectable intervention sub-action, i.e. a PlanDefinition action node
 * returned by `Intervene.getInterventionSubActions`.
 */
type InterventionSubAction = {
  description?: string;
  id?: string;
  title?: string;
};

/**
 * Props for {@link Intervention}, as passed by `careplan/steps/index.tsx`.
 *
 * `condition` is the FLATTENED view, as for `Assessment`. See `careplan/types.ts`.
 */
type InterventionProps = StepPatientIdentity &
  StepCarePlanContext & {
    /** The flattened screening condition; only `verificationStatusCode` is read. */
    condition?: StepConditionFlat;
    /** The `/Task/?patient=...` bundle's `entry` array, scanned for prior interventions. */
    tasks?: StepTasks;
    /** The org's questionnaire bundle, searched for the intervention questionnaire. */
    questionnaireData?: Bundle | undefined;
    /**
     * The intervention action chosen on the assess step, or the one already
     * recorded on the CarePlan. `steps/index.tsx` passes
     * `plannedInterventionId ?? intervention`, both of which can be absent.
     */
    plannedInterventionActionId?: string | undefined;
    /** Re-runs the parent's care-plan queries after an intervention is scheduled. */
    onIntervention: () => void;
    /**
     * Opens the survey sidebar for the intervention questionnaire.
     *
     * Optional to match the hubs it is forwarded through, but invoked unguarded
     * from the survey label's `onClick` below.
     */
    toggleSurveySidebar?: ((questionnaireId: string) => void) | undefined;
    /** Revalidates the email/SMS communication log. */
    communicationsMutate: StepMutate;
    /** Active referrals; used only as a `useEffect` dependency to re-fetch. */
    referralData?: unknown;
  };

const REFERRAL_OBSERVATION_CODE = 'REFERRAL-TYPE';
function Intervention(props: InterventionProps) {
  const authInfo = useAuthContext();
  const plannedInterventionActionId = props.plannedInterventionActionId;

  const [loading, setLoading] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [selectedInterventionSubAction, setSelectedInterventionSubAction] =
    React.useState<InterventionSubAction | null>(null);
  const [popOpen, setpopOpen] = React.useState(false);
  // The selected referral's `referral-id` identifier value, taken off the first
  // element of the Dropdown's selection.
  //
  // `undefined` is in the type as well as `null`: the referral Dropdown is
  // `clearable`, so clearing it calls `handleSelectReferral` with an empty
  // selection and writes `undefined` here. The two absent values are then
  // distinguishable only by which path produced them, which is why the guard
  // below is a plain truthiness test.
  const [currentReferral, setCurrentReferral] = React.useState<string | null | undefined>(null);
  const [notificationFlag, setNotificationFlag] = React.useState(false);
  const [interventionExistFlag, setInterventionExistFlag] = React.useState(false);
  // Seeded `''` but written from `InterventionSubAction['title']`, which the
  // PlanDefinition leaves optional — so `undefined` is genuinely storable here.
  // It reaches the confirmation Modal's `<b>{selectedIntervention}</b>`, which
  // renders nothing for `undefined` rather than failing.
  const [selectedIntervention, setSelectedIntervention] = React.useState<string | undefined>('');
  const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
  };
  const [availableSurvey, setAvailableSurvey] = React.useState(false);

  const {
    data: referralData,
    error: _referralError,
    mutate: referralMutate,
  } = useSWR<fhir4.Bundle, Error>(
    [
      `/Observation/?patient=${props.patientId}&code=${REFERRAL_OBSERVATION_CODE}&identifier:not=https://projectwell.io/fhir/identifiers/referral-status|ENTERED-IN-ERROR`,
      authInfo.token,
    ],
    // `FHIRFetcher` resolves `Bundle | undefined` for an empty endpoint, while swr
    // 0.5.7's fetcher parameter declares no `undefined`.
    FHIRFetcher<fhir4.Bundle> as () => Promise<fhir4.Bundle>,
    options,
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
    referralMutate();
  }, [props.referralData]);

  // `/Observation/?...&code=REFERRAL` is a single-type search, so narrowing to
  // Observation removes nothing at runtime and gives `identifier` a type.
  //
  // SUSPECTED DEFECT — two pre-existing bugs in this predicate, left exactly as
  // found because fixing either changes which referrals the intervene step
  // shows:
  //   1. `(obj.system = '...')` is an assignment, not a comparison. It mutates
  //      every identifier's `system` and always returns a truthy string, so
  //      `findIndex` always returns 0 — the intended lookup by
  //      `.../referral-status` never happens.
  //   2. `x === 'NEW' || 'IN-PROGRESS' || ...` parses as
  //      `(x === 'NEW') || 'IN-PROGRESS' || ...`, and the bare non-empty string
  //      literals are always truthy, so the `if` is unconditionally true and no
  //      referral is ever filtered out by status.
  // `identifier[0]` is asserted rather than guarded: the FHIR server populates
  // `Observation.identifier` for every referral observation this query matches,
  // and defaulting would silently admit a referral with no identifier.
  // Cast on the optional chain, not `bundleEntriesOf`. That helper does TWO things upstream does
  // not: it calls `Array.prototype.filter` to drop entries whose `resource` is absent or of
  // another type, and it substitutes `[]` for a missing `entry` — so `activeReferralData` became
  // `[]` where upstream leaves it `undefined`, and the `activeReferralData?.map` below stopped
  // short-circuiting. Upstream is `referralData?.entry?.filter(...)`; this is that, narrowed.
  const activeReferralData = (
    referralData?.entry as ResourceEntry<'Observation'>[] | undefined
  )?.filter((t) => {
    const index = t.resource.identifier!.findIndex(
      (obj) => (obj.system = 'https://projectwell.io/fhir/identifiers/referral-status'),
    );
    // The bare string literals are always truthy, so this `if` never filters anything —
    // KNOWN_BUGS.md CP-2. Kept verbatim; making the comparison work would change which
    // referrals are listed.
    if (
      // eslint-disable-next-line no-constant-condition, no-constant-binary-expression
      t.resource.identifier![index]!.value === 'NEW' ||
      'IN-PROGRESS' ||
      'OUTREACH' ||
      'PENDING'
    ) {
      return t;
    }
  });
  const handlePopOpen = () => {
    setpopOpen(true);
  };
  const handleClose = () => {
    setpopOpen(false);
  };
  const handleSelectReferral = (
    _e: React.SyntheticEvent<HTMLElement>,
    { value }: DropdownProps,
  ) => {
    // `multiple` Dropdown, so `value` is the selected array; the component tracks only the first.
    setCurrentReferral((value as string[])[0]);
  };
  //const handleClick = () => setConfirmOpen(true);
  const closeConfirm = () => setConfirmOpen(false);
  // As in the assess step: the Dropdown's option `value` is the whole action
  // node object, which `DropdownProps['value']` types as a primitive. The
  // component hands back exactly what it was given, so the double assertion
  // records that round trip.
  const handleSelect = (_e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
    setSelectedInterventionSubAction(value as unknown as InterventionSubAction);
  };

  const handleSubmit = (_e: React.FormEvent<HTMLFormElement>) => {
    setpopOpen(false);
    setConfirmOpen(true);
  };

  const carePlan = props.carePlan;
  let flatCarePlan: FlatCarePlan | undefined;
  if (carePlan) {
    flatCarePlan = FHIRUtils.parseFHIRCarePlan(carePlan);
  }

  //get parent intervenetion Task
  let interventionTaskId: string | undefined;
  const interventionTaskRef: string = fhirpath
    .evaluate(
      props.carePlan,
      "activity.where(reference.display='Intervention').reference.reference",
    )
    .toString();
  if (interventionTaskRef) {
    interventionTaskId = interventionTaskRef.substring(interventionTaskRef.indexOf('/') + 1);
  }

  // `getInterventionSubActions` returns `fhirpath.evaluate(...)`, declared
  // `unknown[]` by `src/fhirpath.d.ts`. The element shape is a claim about the
  // PlanDefinition's intervention sub-action nodes, which is what the filter and
  // the Dropdown below read `id`/`title`/`description` off.
  //
  // `planDefinition` is optional on the props while the helper requires it;
  // `fhirpath.evaluate(undefined, ...)` returns `[]` rather than throwing (probed
  // against fhirpath 2.14.7), so the `!` states the reachability this code
  // already assumes without changing the result.
  let interventionSubActions = Intervene.getInterventionSubActions(
    props.planDefinition!,
    plannedInterventionActionId,
  ) as InterventionSubAction[];
  //use allInterventionSubActions to get previous intervention
  const allInterventionSubActions = interventionSubActions;
  if (currentReferral) {
    let selectedReferralPlan = '';
    //get the referal plan name according to currentReferral
    // The `?.` is retained and inert: `activeReferralData` is always an array,
    // and the callback only assigns the local `selectedReferralPlan`.
    //
    // `valueString` is asserted rather than defaulted to `''`: a default would
    // silently select the fall-through plan in the literal comparisons below
    // instead of failing.
    activeReferralData?.map((referral) => {
      const index = referral.resource.identifier!.findIndex(
        (element) => element.system === 'https://projectwell.io/fhir/identifiers/referral-id',
      );
      if (referral.resource.identifier![index]!.value === currentReferral) {
        selectedReferralPlan = referral.resource.valueString!;
      }
    });
    //filter the intervention plan according to currentReferral
    interventionSubActions = interventionSubActions.filter((subAction) => {
      //for "Monthly Groceries" referal plan, need to select "BeWell Monthly Groceries" plan
      if (
        selectedReferralPlan === 'Monthly Groceries' &&
        subAction.title === 'BeWell Monthly Groceries'
      ) {
        return subAction;
      }
      return selectedReferralPlan === subAction.title;
    });
  }

  //get matching questionnaire from planDef
  const matchingQuestionnaire = fhirpath
    .evaluate(props.planDefinition, "action.where(id = 'intervention').definitionCanonical")
    .toString();
  const matchingQuestionnaireId = matchingQuestionnaire.substring(
    matchingQuestionnaire.indexOf('/') + 1,
  );

  useEffect(() => {
    // Narrowed to `Questionnaire` for the same reason as in the screen and
    // assess steps: only that member of the `FhirResource` union carries `item`,
    // and the `Object.keys(...).includes('item')` below is this code's own check.
    const questionnaire = (props.questionnaireData?.entry?.filter(
      (questionnaire: BundleEntry) => questionnaire?.resource?.id === matchingQuestionnaireId,
    )[0] &&
      props.questionnaireData?.entry?.filter(
        (questionnaire: BundleEntry) => questionnaire?.resource?.id === matchingQuestionnaireId,
      )?.[0]?.resource) as Questionnaire | undefined;
    if (
      questionnaire &&
      Object.keys(questionnaire)?.includes('item') &&
      questionnaire?.item?.length !== 0
    ) {
      setAvailableSurvey(true);
    } else {
      setAvailableSurvey(false);
    }
  }, [matchingQuestionnaireId, props.questionnaireData]);

  const availableReasonCodes: string[] = [];
  let availableInterventions: (string | undefined)[] = [];
  //Get Assessment Task to get who updated it and when
  if (props.tasks) {
    for (let i = 0; i < props.tasks.length; i++) {
      // `!` rather than a guard: `i` is bounded by `props.tasks.length`.
      const task = props.tasks[i]!.resource;
      availableReasonCodes.push(FHIRUtils.parseFHIRTaskReasonCode(task));
    }
    const interventions = availableReasonCodes.map((reasonCode) => {
      //resoncode ex: be-well-monthly-groceries | deliver-pantry-kit. need to only get the plan "be-well-monthly-groceries"
      for (let intervention = 0; allInterventionSubActions.length > intervention; intervention++) {
        // `!` rather than a guard: `intervention` is bounded by the loop condition.
        if (reasonCode?.split('|')?.[0]?.trim() === allInterventionSubActions[intervention]!.id) {
          return allInterventionSubActions[intervention]!.title;
        }
      }
    });
    availableInterventions = interventions.filter((c, index) => {
      if (c && c !== undefined) return interventions.indexOf(c) === index;
    });
  }

  // `async` with no `await`: it is passed straight to `Confirm`'s `onConfirm`, which
  // ignores the returned promise. Kept — dropping `async` changes what the handler returns.
  // eslint-disable-next-line @typescript-eslint/require-await
  const scheduleInternvention = async () => {
    setConfirmOpen(false);
    const lastHalfHour = moment.utc().subtract(30, 'minutes').format(DATE_TIME_FORMAT);
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);
    setLoading(true);
    FHIRFetcherOnDemand(
      `/Observation/?patient=${props.patientId}&code=${REFERRAL_OBSERVATION_CODE}&identifier=IN-PROGRESS&date=gt${lastHalfHour}&_count=1`,
      authInfo.token,
    ).then(
      async (data) => {
        if (
          data?.entry &&
          data.entry.length > 0 &&
          data.entry[0]?.resource?.valueString === selectedInterventionSubAction?.description
        ) {
          console.log('Referral already exists');
          setLoading(false);
          setInterventionExistFlag(true);
          return;
        } else {
          console.log('Referral does not exist');

          // SUSPECTED DEFECT — same shape as the assess step's
          // `Assess.planIntervention` call: `plannedIterventionTaskId` is a
          // required `string` on `scheduleIntervention`, but
          // `interventionTaskId` is assigned only when the CarePlan carries an
          // `activity.where(reference.display='Intervention')`. When it does
          // not, `CarePlanBuilder.buildSubTasks` writes
          // `partOfRef = 'Task/' + parentTaskId` as the literal
          // `"Task/undefined"` rather than failing.
          //
          // `currentReferral` is `string | null | undefined` while
          // `scheduleIntervention` declares `referralId?: string`. `null` is
          // passed today and is benign — `TaskBuilder.build` guards it with
          // `if (referralId)`, so `null` and `undefined` behave identically —
          // but the declared type is narrower than what the call site supplies.
          try {
            await Intervene.scheduleIntervention(
              props.orgId!,
              selectedInterventionSubAction as unknown as PlanDefinitionAction,
              props.patientId!,
              flatCarePlan!.id,
              flatCarePlan!.careTeamId,
              interventionTaskId!,
              moment().format('YYYY-MM-DD'),
              {
                reference: 'Practitioner/' + authInfo.practitionerId,
                display: authInfo.practitionerName,
              },
              authInfo.practitionerName,
              fhirProxyClient,
              currentReferral as string | undefined,
            );

            // Align with reactivating a cancelled intervention task: mark member ACTIVE / ENROLL
            await HooksPoster(`/hooks/patient/status`, authInfo.token, {
              patientId: props.patientId,
              status: 'ACTIVE',
              reason: 'ENROLL',
              taskId: interventionTaskId,
            });

            setSelectedIntervention(selectedInterventionSubAction!.title);
            setNotificationFlag(true);

            const orderEmailName = REFERRAL_TYPE_TO_EMAIL[
              mapKey(selectedInterventionSubAction!.title)
            ] as string | undefined;

            // send hA welcome/thank you email survey (#2a-e)
            if (props.orgId === 'HA' && orderEmailName !== null && orderEmailName !== undefined) {
              setLoading(true);

              // Both branches below assign an object literal and then add
              // `phoneNumber` conditionally, so the binding carries an index signature.
              let payload: Record<string, unknown>,
                textPayload: Record<string, unknown> | undefined;
              if (orderEmailName.includes('SURVEY')) {
                // send web survey email
                payload = {
                  patientId: props.patientId,
                  subject: REFERRAL_SUBJECT_LINES[orderEmailName],
                  sender: authInfo.practitionerName,
                  memberName: props.patientName,
                  email: props.patientEmail,
                  emailTemplate: EMAIL_TEMPLATES[orderEmailName],
                  questionnaireId: REFERRAL_SURVEYS[orderEmailName],
                  text: 'intervention launch',
                  templateName: TEXT_MESSAGE_TEMPLATES_PINPOINT?.[orderEmailName],
                  emailTitle: orderEmailName,
                };
                if (props.patientPhone) {
                  // SUSPECTED DEFECT — REPORTED, NOT FIXED. `patientPhone` is the raw
                  // fhirpath COLLECTION, so this concatenates an array: one entry works
                  // by accident, two produce "+15551234,5559999". Recorded as
                  // KNOWN_BUGS.md CP-27. Cast, not indexed — picking `[0]` would change
                  // which number is sent.
                  payload['phoneNumber'] = ('+1' + (props.patientPhone as string)).replaceAll('-', '');
                }
              } else {
                // send regular email
                payload = {
                  to: props.patientEmail,
                  from: 'food@nourishedrx.com',
                  patientId: props.patientId,
                  templateId: EMAIL_TEMPLATES[orderEmailName],
                  subject: orderEmailName,
                  sender: authInfo.practitionerName,
                  dynamicTemplateData: {
                    memberName: props.patientName,
                  },
                  emailTitle: orderEmailName,
                  text: 'intervention launch',
                };
                if (props.patientPhone) {
                  textPayload = {
                    // Same array-concatenation defect as the site above.
                    phoneNumber: ('+1' + (props.patientPhone as string)).replaceAll('-', ''),
                    patientId: props.patientId,
                    message: REFERRAL_SUBJECT_LINES[orderEmailName],
                    subject: orderEmailName,
                    sender: authInfo.practitionerName,
                    text: 'intervention launch',
                    templateName: TEXT_MESSAGE_TEMPLATES_PINPOINT?.[orderEmailName],
                  };
                }
              }
              await SendGridPoster(
                orderEmailName.includes('SURVEY') ? `/hooks/web-survey` : `/hooks/mail`,
                authInfo.token,
                payload,
              ).then(
                () => {
                  setLoading(false);
                  props.communicationsMutate();
                },
                (e) => {
                  setLoading(false);
                  console.log(`Error: Failed to send email: ${(e as Error).toString()}`);
                },
              );
              if (textPayload) {
                await SendGridPoster(`/hooks/text`, authInfo.token, textPayload).then(
                  () => {
                    setLoading(false);
                    props.communicationsMutate();
                  },
                  (e) => {
                    setLoading(false);
                    console.log(`Error: Failed to send text: ${(e as Error).toString()}`);
                  },
                );
              }
            }

            // The intervention tasks are written synchronously, but the FHIR search
            // index is eventually-consistent, so a single delayed read can miss them
            // and leave the list stale until a manual reload. Refresh a few times so a
            // later read reliably reflects the newly indexed tasks.
            const refreshDelaysMs = [4000, 8000, 12000];
            refreshDelaysMs.forEach((delay, index) => {
              setTimeout(() => {
                props.onIntervention(); // callback
                if (index === refreshDelaysMs.length - 1) {
                  setLoading(false);
                }
              }, delay);
            });
          } catch (error) {
            console.log(
              'Error in Scheduling Intervention Sub Action',
              error instanceof Error ? error.message : String(error),
            );
            setLoading(false);
            throw error;
          }
        }
      },
      (error) => {
        console.log('Latest Referral Error', error);
      },
    );

    // setLoading(false);
    // props.onIntervention(); //callback
  };

  /**
   * Fetches a FHIR endpoint through the `/fhir` proxy, outside SWR.
   *
   * A local twin of `data/FHIRFetcher`, needed because the caller runs inside a
   * click handler rather than at render. The type argument is an unchecked claim
   * confined to this boundary; the payload is unvalidated JSON.
   *
   * The entry type is narrowed to `ResourceEntry<'Observation'>` because the one
   * caller queries `/Observation/?…` and reads `valueString`, which the bare
   * `FhirResource` union does not carry. A claim about the endpoint, not a
   * check (see `utils/bundleNarrowing`).
   */
  const FHIRFetcherOnDemand = async (endPoint: string, token: string) => {
    const dataEndPoint = '/fhir' + endPoint;

    const data = await apiClient.get<fhir4.Bundle & { entry?: ResourceEntry<'Observation'>[] }>(
      'fhir-proxy',
      dataEndPoint,
      {
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
      },
    );
    return data;
  };

  return (
    <>
      {loading && <Loader active inline="centered" />}
      <List horizontal>
        <>
          <Label
            color={
              props.condition?.verificationStatusCode === 'confirmed' && plannedInterventionActionId
                ? 'blue'
                : 'grey'
            }
            image
            className={availableSurvey ? '' : 'ui disabled button'}
          >
            <Icon name="file" />
            <Label.Detail
              as="a"
              onClick={() => props.toggleSurveySidebar!(matchingQuestionnaireId)}
            >
              {availableSurvey ? 'Take Survey' : 'No Available Survey'}
            </Label.Detail>
          </Label>
          <Label
            color={
              props.condition?.verificationStatusCode === 'confirmed' && plannedInterventionActionId
                ? 'blue'
                : 'grey'
            }
            image
          >
            <Icon name="check" />
            <Popup
              flowing
              position="top right"
              trigger={
                <Label.Detail color={props.carePlan ? 'blue' : 'grey'} as="a">
                  Choose Intervention
                </Label.Detail>
              }
              content={
                <div>
                  <div style={{ marginBottom: '10px' }}>
                    <h4 style={{ color: '#2185D0' }}>Previous Interventions: </h4>
                    <List bulleted>
                      {availableInterventions.map((intervention) => (
                        <List.Item>{intervention}</List.Item>
                      ))}
                    </List>
                  </div>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group>
                      {props.orgId === 'HA' && (
                        <Form.Dropdown
                          placeholder="Select Referral ID"
                          selection
                          clearable
                          options={
                            (referralData &&
                              referralData.entry &&
                              // The `referralData && referralData.entry &&` guard
                              // is kept rather than folded into the helper: it
                              // makes `options` falsy (not `[]`) when the bundle
                              // has not arrived, which is what this Dropdown
                              // renders against today.
                              //
                              // `FlatReferral['referralId']` is the raw
                              // `fhirpath.evaluate` result — an `unknown[]`, not a
                              // string — so the option's key/text/value are arrays
                              // that semantic-ui and React coerce when rendering.
                              // The assertion records that. It also absorbs the
                              // guard's falsy result: `FormDropdownProps['options']`
                              // rejects an explicit `undefined` under
                              // `exactOptionalPropertyTypes`, and widening the
                              // annotation there is what the prop refuses.
                              // Cast, not `bundleEntriesOf` — upstream is
                              // `referralData.entry.map(...)` under this same guard.
                              (referralData.entry as ResourceEntry<'Observation'>[]).map(
                                (referral) => {
                                  const flatReferral = FHIRUtils.parseFHIRreferral(
                                    referral.resource,
                                  );
                                  return {
                                    key: flatReferral.referralId,
                                    text: flatReferral.referralId,
                                    value: flatReferral.referralId,
                                  };
                                },
                              )) as unknown as DropdownItemProps[]
                          }
                          onChange={handleSelectReferral}
                        />
                      )}

                      <Form.Dropdown
                        placeholder="Select Intervention Action"
                        selection
                        // The producing half of the object-valued Dropdown
                        // documented at `handleSelect`.
                        options={
                          interventionSubActions.map((interventionSubAction) => {
                            return {
                              key: interventionSubAction.id,
                              text: interventionSubAction.title,
                              value: interventionSubAction,
                            };
                          }) as unknown as DropdownItemProps[]
                        }
                        onChange={handleSelect}
                      />
                      <Form.Button primary content="Schedule" />
                    </Form.Group>
                  </Form>
                </div>
              }
              on="click"
              open={popOpen}
              onOpen={handlePopOpen}
              onClose={handleClose}
            />
          </Label>

          <Confirm
            header={`Schedule Intervention: ${selectedInterventionSubAction?.title}!`}
            open={confirmOpen}
            onCancel={closeConfirm}
            onConfirm={scheduleInternvention}
          />
        </>
      </List>
      {notificationFlag && (
        <Modal
          open={notificationFlag}
          size="mini"
          content={
            <>
              <Modal.Header>
                <Icon name="thumbs up outline" size="large" color="orange"></Icon>
                Intervention Subscription
              </Modal.Header>
              <Modal.Content>
                {
                  <span>
                    Subscribed for this intervention: <b>{selectedIntervention}</b>{' '}
                  </span>
                }
              </Modal.Content>
            </>
          }
          onActionClick={() => setNotificationFlag(false)}
          actions={[{ key: 'done', icon: 'check', content: 'OK', positive: true }]}
        ></Modal>
      )}
      {interventionExistFlag && (
        <Modal
          open={interventionExistFlag}
          size="mini"
          content={
            <>
              <Modal.Header>
                <Icon name="thumbs up outline" size="large" color="orange"></Icon>
                Intervention already existed
              </Modal.Header>
              <Modal.Content>
                {
                  <span>
                    Already subscribed for this intervention: <b>{selectedIntervention}</b>{' '}
                  </span>
                }
              </Modal.Content>
            </>
          }
          onActionClick={() => setInterventionExistFlag(false)}
          actions={[{ key: 'done', icon: 'check', content: 'OK', positive: true }]}
        ></Modal>
      )}
    </>
  );
}

export default Intervention;
